import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import { GestureRecognizer, FilesetResolver } from '@mediapipe/tasks-vision';

import { AppWrap, MotionWrap } from '../../wrapper';
import { urlFor, client } from '../../client';
import './Reviews.scss';

const Reviews = () => {
  // State management
  const [reviews, setReviews] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [gestureRecognizer, setGestureRecognizer] = useState(null);
  const [isModelLoading, setIsModelLoading] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [modelReady, setModelReady] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [loadingStage, setLoadingStage] = useState('');
  const [detectedGesture, setDetectedGesture] = useState(null);
  const [showGestureSuccess, setShowGestureSuccess] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [capturedPhoto, setCapturedPhoto] = useState(null);
  const [gestureConfirmCount, setGestureConfirmCount] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [liveGesture, setLiveGesture] = useState(null);
  const [gestureScore, setGestureScore] = useState(0);
  const [handDetected, setHandDetected] = useState(false);
  const [frameCount, setFrameCount] = useState(0);
  const [allGestures, setAllGestures] = useState([]);
  const [reviewType, setReviewType] = useState(null); // 'public' or 'private'
  const frameCountRef = useRef(0);
  const lastGestureRef = useRef(null);
  const isRecordingRef = useRef(false); // Use ref to avoid stale closure in detection loop
  const gestureConfirmCountRef = useRef(0); // Track confirm count in ref for reliable counting
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    relationship: '',
    company: '',
    description: '',
    rating: 5,
    includePhoto: false
  });
  
  // Relationship options
  const relationshipOptions = [
    { value: 'colleague', label: 'Colleague' },
    { value: 'client', label: 'Client' },
    { value: 'manager', label: 'Manager' },
    { value: 'direct-report', label: 'Direct Report' },
    { value: 'collaborator', label: 'Collaborator' },
    { value: 'mentee', label: 'Mentee' }
  ];
  
  // Refs
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const animationRef = useRef(null);
  
  // Fetch existing public reviews only
  useEffect(() => {
    const query = '*[_type == "reviews" && isPublic == true] | order(createdAt desc)';
    client.fetch(query).then((data) => {
      setReviews(data);
    });
  }, [submitSuccess]);
  
  // Initialize MediaPipe Gesture Recognizer
  const initializeGestureRecognizer = async () => {
    setIsModelLoading(true);
    setModelReady(false);
    try {
      setLoadingStage('vision');
      console.log('Loading MediaPipe Vision...');
      
      const vision = await FilesetResolver.forVisionTasks(
        'https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@latest/wasm'
      );
      
      setLoadingStage('gesture');
      console.log('Loading Gesture Recognizer model...');
      
      const recognizer = await GestureRecognizer.createFromOptions(vision, {
        baseOptions: {
          modelAssetPath: 'https://storage.googleapis.com/mediapipe-models/gesture_recognizer/gesture_recognizer/float16/1/gesture_recognizer.task',
          delegate: 'GPU'
        },
        runningMode: 'VIDEO',
        numHands: 1
      });
      
      console.log('Gesture Recognizer created successfully');
      setGestureRecognizer(recognizer);
      
      setLoadingStage('ready');
      setModelReady(true);
      setIsModelLoading(false);
      
      await new Promise(resolve => setTimeout(resolve, 500));
      
      return recognizer;
    } catch (error) {
      console.error('Error initializing Gesture Recognizer:', error);
      setLoadingStage('error');
      setIsModelLoading(false);
      return null;
    }
  };
  
  // Start webcam
  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        streamRef.current = stream;
      }
      
      return true;
    } catch (error) {
      console.error('Error accessing webcam:', error);
      alert('Could not access webcam. Please ensure you have granted camera permissions.');
      return false;
    }
  };
  
  // Stop webcam
  const stopWebcam = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
    if (animationRef.current) {
      cancelAnimationFrame(animationRef.current);
      animationRef.current = null;
    }
  };
  
  // Detect gestures in video frame
  const detectGestures = useCallback((recognizer) => {
    // Use ref to check if still recording (avoids stale closure)
    if (!isRecordingRef.current) {
      console.log('Detection stopped - not recording');
      return;
    }
    
    if (!videoRef.current || !recognizer) {
      animationRef.current = requestAnimationFrame(() => detectGestures(recognizer));
      return;
    }
    
    const video = videoRef.current;
    
    if (video.readyState < 2) {
      animationRef.current = requestAnimationFrame(() => detectGestures(recognizer));
      return;
    }
    
    try {
      frameCountRef.current += 1;
      if (frameCountRef.current % 10 === 0) {
        setFrameCount(frameCountRef.current);
      }
      
      const nowInMs = performance.now();
      const results = recognizer.recognizeForVideo(video, nowInMs);
      
      // Draw landmarks on canvas
      if (canvasRef.current) {
        const canvas = canvasRef.current;
        const ctx = canvas.getContext('2d');
        
        if (canvas.width !== video.videoWidth || canvas.height !== video.videoHeight) {
          canvas.width = video.videoWidth;
          canvas.height = video.videoHeight;
        }
        
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        
        if (results.landmarks && results.landmarks.length > 0) {
          setHandDetected(true);
          
          // Draw hand landmarks
          for (const landmarks of results.landmarks) {
            // Draw connections
            ctx.shadowBlur = 15;
            ctx.shadowColor = '#10b981';
            ctx.strokeStyle = '#10b981';
            ctx.lineWidth = 4;
            ctx.lineCap = 'round';
            
            const connections = [
              [0, 1], [1, 2], [2, 3], [3, 4], // thumb
              [0, 5], [5, 6], [6, 7], [7, 8], // index
              [5, 9], [9, 10], [10, 11], [11, 12], // middle
              [9, 13], [13, 14], [14, 15], [15, 16], // ring
              [13, 17], [17, 18], [18, 19], [19, 20], // pinky
              [0, 17] // palm
            ];
            
            connections.forEach(([i, j]) => {
              const start = landmarks[i];
              const end = landmarks[j];
              ctx.beginPath();
              ctx.moveTo(start.x * canvas.width, start.y * canvas.height);
              ctx.lineTo(end.x * canvas.width, end.y * canvas.height);
              ctx.stroke();
            });
            
            // Draw points
            ctx.shadowBlur = 20;
            ctx.shadowColor = '#06b6d4';
            ctx.fillStyle = '#06b6d4';
            
            landmarks.forEach((point, idx) => {
              ctx.beginPath();
              const radius = (idx === 0 || idx === 4) ? 10 : 6;
              ctx.arc(point.x * canvas.width, point.y * canvas.height, radius, 0, 2 * Math.PI);
              ctx.fill();
            });
          }
          
          // Process gesture results
          let gesture = null;
          let score = 0;
          
          if (results.gestures && results.gestures.length > 0) {
            const topGesture = results.gestures[0][0];
            const gestureName = topGesture.categoryName;
            score = topGesture.score * 100;
            
            setAllGestures(results.gestures[0].map(g => ({
              name: g.categoryName,
              score: (g.score * 100).toFixed(1) + '%'
            })));
            
            // Map MediaPipe gesture names to our format
            if (gestureName === 'Thumb_Up') {
              gesture = 'thumbsUp';
            } else if (gestureName === 'Thumb_Down') {
              gesture = 'thumbsDown';
            }
            
            setGestureScore(score);
          }
          
          setLiveGesture(gesture);
          
          if (gesture) {
            // Highlight thumb with gesture color
            const thumbTip = results.landmarks[0][4];
            ctx.shadowBlur = 30;
            ctx.shadowColor = gesture === 'thumbsUp' ? '#22c55e' : '#f59e0b';
            ctx.fillStyle = gesture === 'thumbsUp' ? '#22c55e' : '#f59e0b';
            ctx.beginPath();
            ctx.arc(thumbTip.x * canvas.width, thumbTip.y * canvas.height, 15, 0, 2 * Math.PI);
            ctx.fill();
            
            // Use ref for consistent gesture tracking
            if (lastGestureRef.current === gesture) {
              gestureConfirmCountRef.current += 1;
              setGestureConfirmCount(gestureConfirmCountRef.current);
              
              console.log(`Gesture "${gesture}" confirmed: ${gestureConfirmCountRef.current}/15`);
              
              if (gestureConfirmCountRef.current >= 15) {
                // Capture complete! Stop detection first
                isRecordingRef.current = false;
                
                // Capture photo
                const tempCanvas = document.createElement('canvas');
                tempCanvas.width = video.videoWidth;
                tempCanvas.height = video.videoHeight;
                const tempCtx = tempCanvas.getContext('2d');
                tempCtx.translate(tempCanvas.width, 0);
                tempCtx.scale(-1, 1);
                tempCtx.drawImage(video, 0, 0);
                const photoData = tempCanvas.toDataURL('image/jpeg', 0.8);
                
                // Update all state
                setDetectedGesture(gesture);
                setCapturedPhoto(photoData);
                stopWebcam();
                setIsRecording(false);
                setShowGestureSuccess(true);
                
                // Reset refs
                gestureConfirmCountRef.current = 0;
                lastGestureRef.current = null;
                
                setTimeout(() => {
                  setShowGestureSuccess(false);
                  setShowForm(true);
                }, 2500);
                
                return; // Exit detection loop
              }
            } else {
              gestureConfirmCountRef.current = 1; // Reset to 1 since this frame counts
              setGestureConfirmCount(1);
            }
            lastGestureRef.current = gesture;
          } else {
            gestureConfirmCountRef.current = 0;
            setGestureConfirmCount(0);
            lastGestureRef.current = null;
          }
        } else {
          setHandDetected(false);
          setLiveGesture(null);
          setGestureScore(0);
          gestureConfirmCountRef.current = 0;
          setGestureConfirmCount(0);
          setAllGestures([]);
          lastGestureRef.current = null;
        }
      }
    } catch (error) {
      console.error('Detection error:', error);
    }
    
    // Continue loop using ref check
    if (isRecordingRef.current) {
      animationRef.current = requestAnimationFrame(() => detectGestures(recognizer));
    }
  }, []);
  
  // Start review process
  const startReview = async (type = 'public') => {
    // Set review type and state
    setReviewType(type);
    setIsRecording(true);
    isRecordingRef.current = true;
    
    setDetectedGesture(null);
    setShowForm(false);
    setShowGestureSuccess(false);
    setCapturedPhoto(null);
    setGestureConfirmCount(0);
    gestureConfirmCountRef.current = 0;
    setLiveGesture(null);
    setGestureScore(0);
    setHandDetected(false);
    setAllGestures([]);
    setFormData({ name: '', relationship: '', company: '', description: '', rating: 5, includePhoto: false });
    
    const webcamStarted = await startWebcam();
    if (!webcamStarted) {
      setIsRecording(false);
      isRecordingRef.current = false;
      return;
    }
    
    let recognizer = gestureRecognizer;
    if (!recognizer) {
      recognizer = await initializeGestureRecognizer();
    }
    
    if (recognizer && videoRef.current) {
      frameCountRef.current = 0;
      setFrameCount(0);
      lastGestureRef.current = null;
      
      const startDetection = () => {
        console.log('Starting gesture detection loop...');
        // Make sure recording ref is still true before starting
        if (isRecordingRef.current) {
          detectGestures(recognizer);
        }
      };
      
      if (videoRef.current.readyState >= 2) {
        startDetection();
      } else {
        videoRef.current.onloadeddata = startDetection;
      }
    }
  };
  
  // Cancel review
  const cancelReview = () => {
    // Stop recording ref first to stop detection loop
    isRecordingRef.current = false;
    
    stopWebcam();
    setIsRecording(false);
    setShowForm(false);
    setShowGestureSuccess(false);
    setDetectedGesture(null);
    setCapturedPhoto(null);
    setGestureConfirmCount(0);
    gestureConfirmCountRef.current = 0;
    setLiveGesture(null);
    setGestureScore(0);
    setHandDetected(false);
    setAllGestures([]);
    setReviewType(null);
    lastGestureRef.current = null;
  };
  
  // Restart gesture detection (keep webcam open)
  const restartDetection = () => {
    // Reset all state
    setDetectedGesture(null);
    setCapturedPhoto(null);
    setGestureConfirmCount(0);
    gestureConfirmCountRef.current = 0;
    setLiveGesture(null);
    setGestureScore(0);
    setAllGestures([]);
    lastGestureRef.current = null;
    frameCountRef.current = 0;
    setFrameCount(0);
    
    // If webcam is stopped, restart everything
    if (!streamRef.current) {
      startReview();
    } else if (gestureRecognizer) {
      // Webcam still running, restart detection loop
      isRecordingRef.current = true;
      console.log('Restarting detection loop...');
      detectGestures(gestureRecognizer);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      let photoAsset = null;
      
      if (formData.includePhoto && capturedPhoto) {
        const blob = await fetch(capturedPhoto).then(r => r.blob());
        photoAsset = await client.assets.upload('image', blob, {
          filename: `review-${Date.now()}.jpg`
        });
      }
      
      const reviewDoc = {
        _type: 'reviews',
        name: formData.name || 'Anonymous',
        relationship: formData.relationship,
        company: formData.company || '',
        description: formData.description || '',
        rating: formData.rating,
        gesture: detectedGesture,
        isPublic: reviewType === 'public',
        createdAt: new Date().toISOString(),
        ...(photoAsset && { photo: { _type: 'image', asset: { _type: 'reference', _ref: photoAsset._id } } })
      };
      
      await client.create(reviewDoc);
      
      setSubmitSuccess(true);
      setShowForm(false);
      setIsRecording(false);
      setDetectedGesture(null);
      setCapturedPhoto(null);
      
      setTimeout(() => setSubmitSuccess(false), 3000);
    } catch (error) {
      console.error('Error submitting review:', error);
      alert('Failed to submit review. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  // Navigate reviews
  const handlePrev = () => {
    setCurrentIndex(prev => (prev === 0 ? reviews.length - 1 : prev - 1));
  };
  
  const handleNext = () => {
    setCurrentIndex(prev => (prev === reviews.length - 1 ? 0 : prev + 1));
  };
  
  // Render star rating
  const renderStars = (rating, interactive = false, onChange = null) => {
    return (
      <div className="stars-container">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            className={`star ${star <= rating ? 'filled' : ''} ${interactive ? 'interactive' : ''}`}
            onClick={() => interactive && onChange && onChange(star)}
          >
            ★
          </span>
        ))}
      </div>
    );
  };

  return (
    <div className="app__reviews">
      {/* Section Header */}
      <motion.div
        className="app__reviews-header"
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <p className="section-subtitle">Experience <span>WebAI</span></p>
        <h2 className="head-text">
          Write a <span>Review</span>
        </h2>
        <p className="reviews-description">
          Use your webcam to leave a thumbs up 👍 or thumbs down 👎 review. 
          Powered by MediaPipe Gesture Recognizer, all processed locally in your browser, no data sent to servers.
        </p>
      </motion.div>

      {/* Review Capture Section */}
      <motion.div
        className="app__reviews-capture"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
      >
        {!isRecording && !showForm && !submitSuccess && !showGestureSuccess && (
          <div className="review-type-buttons">
            <motion.button
              className="start-review-btn public-btn"
              onClick={() => startReview('public')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isModelLoading}
            >
              <span className="btn-bg"></span>
              <span className="btn-content">
                {isModelLoading ? (
                  <div className="loading-stages">
                    <span className="loading-spinner"></span>
                    <span className="stage-text">Loading...</span>
                  </div>
                ) : (
                  <>
                    <span className="btn-icon">📢</span>
                    <span>Write a Public Review</span>
                  </>
                )}
              </span>
            </motion.button>
            
            <motion.button
              className="start-review-btn private-btn"
              onClick={() => startReview('private')}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              disabled={isModelLoading}
            >
              <span className="btn-bg"></span>
              <span className="btn-content">
                {isModelLoading ? (
                  <div className="loading-stages">
                    <span className="loading-spinner"></span>
                    <span className="stage-text">Loading...</span>
                  </div>
                ) : (
                  <>
                    <span className="btn-icon">🔒</span>
                    <span>Send Private Feedback</span>
                  </>
                )}
              </span>
            </motion.button>
          </div>
        )}

        {/* Webcam View */}
        <AnimatePresence>
          {isRecording && !showForm && (
            <motion.div
              className="webcam-container"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <video
                ref={videoRef}
                autoPlay
                playsInline
                muted
                className="webcam-video"
              />
              <canvas 
                ref={canvasRef} 
                className="webcam-overlay-canvas"
                width={640}
                height={480}
              />
              
              {/* Model Info Panel */}
              <div className="model-info-panel">
                <div className="info-header">
                  <span className={`pulse-dot ${handDetected ? 'active' : ''}`}></span>
                  <h4>AI Model Active</h4>
                </div>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">Engine:</span>
                    <span className="value">MediaPipe</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Model:</span>
                    <span className="value">Gesture Recognizer</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Runtime:</span>
                    <span className="value">WASM + GPU</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Frames:</span>
                    <span className="value">{frameCount}</span>
                  </div>
                  <div className="info-item status">
                    <span className="label">Hand:</span>
                    <span className={`value ${handDetected ? 'detected' : 'searching'}`}>
                      {handDetected ? '✓ Detected' : 'Scanning...'}
                    </span>
                  </div>
                  {gestureScore > 0 && (
                    <div className="info-item score">
                      <span className="label">Confidence:</span>
                      <span className="value">{gestureScore.toFixed(0)}%</span>
                    </div>
                  )}
                  {liveGesture && (
                    <div className="info-item gesture">
                      <span className="label">Gesture:</span>
                      <span className={`value ${liveGesture}`}>
                        {liveGesture === 'thumbsUp' ? '👍 Thumb Up' : '👎 Thumb Down'}
                      </span>
                    </div>
                  )}
                </div>
                
                {/* All Detected Gestures */}
                {allGestures.length > 0 && (
                  <div className="pose-data">
                    <div className="pose-header">Detected Gestures:</div>
                    {allGestures.slice(0, 3).map((g, idx) => (
                      <div key={idx} className="finger-data">
                        <span className="finger-name">{g.name}:</span>
                        <span className="finger-curl">{g.score}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="gesture-overlay">
                {/* Central hold indicator - big and prominent when gesture detected */}
                {liveGesture && (
                  <div className={`central-hold-indicator ${liveGesture}`}>
                    <span className="hold-emoji">{liveGesture === 'thumbsUp' ? '👍' : '👎'}</span>
                    <div className="hold-content">
                      <span className="hold-title">HOLD STEADY!</span>
                      <span className="hold-confidence">{gestureScore.toFixed(0)}% confident</span>
                      <div className="hold-progress-ring">
                        <svg viewBox="0 0 36 36">
                          <path
                            className="ring-bg"
                            d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                          <path
                            className={`ring-progress ${liveGesture}`}
                            strokeDasharray={`${(gestureConfirmCount / 15) * 100}, 100`}
                            d="M18 2.0845a 15.9155 15.9155 0 0 1 0 31.831a 15.9155 15.9155 0 0 1 0 -31.831"
                          />
                        </svg>
                        <span className="countdown">{Math.max(1, Math.ceil((15 - gestureConfirmCount) / 5))}</span>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Bottom instruction bar */}
                <div className="bottom-instructions">
                  {!liveGesture && (
                    <div className="gesture-hints">
                      <span className={`gesture-icon ${liveGesture === 'thumbsUp' ? 'active' : ''}`}>👍</span>
                      <span className="or-text">or</span>
                      <span className={`gesture-icon ${liveGesture === 'thumbsDown' ? 'active' : ''}`}>👎</span>
                    </div>
                  )}
                  
                  <p className={`status-text ${handDetected ? 'hand-detected' : ''}`}>
                    {handDetected 
                      ? (liveGesture ? `${liveGesture === 'thumbsUp' ? 'Thumbs Up' : 'Thumbs Down'} detected!` : 'Hand detected — show thumbs up or down')
                      : 'Show your hand to the camera'}
                  </p>
                  
                  <div className="detection-bar">
                    <div 
                      className={`detection-progress ${liveGesture} ${gestureConfirmCount > 0 ? 'active' : ''}`}
                      style={{ width: `${(gestureConfirmCount / 15) * 100}%` }}
                    />
                  </div>
                </div>
              </div>
              <div className="webcam-actions">
                <button className="restart-btn" onClick={restartDetection}>
                  ↻ Restart
                </button>
                <button className="cancel-btn" onClick={cancelReview}>
                  Cancel
                </button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Gesture Capture Success Animation */}
        <AnimatePresence>
          {showGestureSuccess && (
            <motion.div
              className={`gesture-capture-success ${detectedGesture}`}
              initial={{ opacity: 0, scale: 0.5 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
              transition={{ type: "spring", duration: 0.5 }}
            >
              <span className="gesture-emoji">
                {detectedGesture === 'thumbsUp' ? '🎉' : '😔'}
              </span>
              <p className={`gesture-message ${detectedGesture}`}>
                {detectedGesture === 'thumbsUp' 
                  ? "Awesome! You loved it!" 
                  : "I'll do better next time!"}
              </p>
              <p className="continue-text">Loading review form...</p>
              <button 
                className="try-again-btn" 
                onClick={() => {
                  setShowGestureSuccess(false);
                  startReview();
                }}
              >
                ↻ Try Again
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Review Form */}
        <AnimatePresence>
          {showForm && (
            <motion.form
              className="review-form"
              onSubmit={handleSubmit}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
            >
              {/* Review Type Badge */}
              <div className={`review-type-badge ${reviewType}`}>
                {reviewType === 'public' ? (
                  <>📢 Public Review — will be displayed on the site</>
                ) : (
                  <>🔒 Private Feedback — only Elisabeth will see this</>
                )}
              </div>
              
              <div className="gesture-result">
                <span className={`gesture-badge ${detectedGesture}`}>
                  {detectedGesture === 'thumbsUp' ? '👍' : '👎'}
                  {detectedGesture === 'thumbsUp' ? ' Thumbs Up!' : ' Thumbs Down'}
                </span>
              </div>

              <div className="form-group">
                <label>How do you know Elisabeth? <span className="required">*</span></label>
                <select
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  required
                  className={formData.relationship ? '' : 'placeholder'}
                >
                  <option value="" disabled>Select your relationship...</option>
                  {relationshipOptions.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Company / Project (optional)</label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  placeholder="Where did you work together?"
                />
              </div>

              <div className="form-group">
                <label>Your Name (optional)</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Anonymous"
                />
              </div>

              <div className="form-group">
                <label>Your Review (optional)</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Share your experience working with Elisabeth..."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Rating</label>
                {renderStars(formData.rating, true, (star) => setFormData({ ...formData, rating: star }))}
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={formData.includePhoto}
                    onChange={(e) => setFormData({ ...formData, includePhoto: e.target.checked })}
                  />
                  <span className="checkmark"></span>
                  Include my photo with this review
                </label>
                {formData.includePhoto && capturedPhoto && (
                  <div className="photo-preview">
                    <img src={capturedPhoto} alt="Captured snapshot" />
                  </div>
                )}
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-form-btn" onClick={cancelReview}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  <span className="btn-bg"></span>
                  <span className="btn-content">
                    {isSubmitting ? 'Submitting...' : 'Submit Review'}
                  </span>
                </button>
              </div>
            </motion.form>
          )}
        </AnimatePresence>

        {/* Success Message */}
        <AnimatePresence>
          {submitSuccess && (
            <motion.div
              className="success-message"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.9 }}
            >
              <span className="success-icon">✓</span>
              <p>Thank you for your review!</p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      {/* Reviews Display */}
      {reviews.length > 0 && (
        <motion.div
          className="app__reviews-display"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.4 }}
        >
          <h3 className="reviews-title">What People Say</h3>
          
          <div className="reviews-carousel">
            <button className="nav-btn prev" onClick={handlePrev}>
              <HiChevronLeft />
            </button>
            
            <AnimatePresence mode="wait">
              <motion.div
                key={currentIndex}
                className="review-card"
                initial={{ opacity: 0, x: 50 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -50 }}
                transition={{ duration: 0.3 }}
              >
                <div className="review-header">
                  <div className="reviewer-photo">
                    {reviews[currentIndex]?.photo ? (
                      <img src={urlFor(reviews[currentIndex].photo)} alt={reviews[currentIndex].name} />
                    ) : (
                      <div className="avatar-placeholder">
                        {(reviews[currentIndex]?.name || 'A')[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <div className="reviewer-info">
                    <h4>{reviews[currentIndex]?.name || 'Anonymous'}</h4>
                    {reviews[currentIndex]?.relationship && (
                      <p className="reviewer-relationship">
                        {relationshipOptions.find(r => r.value === reviews[currentIndex].relationship)?.label || reviews[currentIndex].relationship}
                        {reviews[currentIndex]?.company && ` at ${reviews[currentIndex].company}`}
                      </p>
                    )}
                    <div className="review-meta">
                      <span className={`gesture-mini ${reviews[currentIndex]?.gesture}`}>
                        {reviews[currentIndex]?.gesture === 'thumbsUp' ? '👍' : '👎'}
                      </span>
                      {renderStars(reviews[currentIndex]?.rating || 5)}
                    </div>
                  </div>
                </div>
                
                {reviews[currentIndex]?.description && (
                  <p className="review-text">"{reviews[currentIndex].description}"</p>
                )}
                
                <p className="review-date">
                  {new Date(reviews[currentIndex]?.createdAt).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric'
                  })}
                </p>
              </motion.div>
            </AnimatePresence>
            
            <button className="nav-btn next" onClick={handleNext}>
              <HiChevronRight />
            </button>
          </div>
          
          <div className="reviews-dots">
            {reviews.map((_, index) => (
              <span
                key={index}
                className={`dot ${index === currentIndex ? 'active' : ''}`}
                onClick={() => setCurrentIndex(index)}
              />
            ))}
          </div>
        </motion.div>
      )}
    </div>
  );
};

export default AppWrap(
  MotionWrap(Reviews, 'app__reviews'),
  'reviews',
  'app__primarybg'
);
